import { observer } from 'owa-mobx-react';
import type { MailListItemWrapperProps } from 'owa-mail-list-item-shared';
import { getStore as getListViewStore, getListViewState } from 'owa-mail-list-store';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { getIsChecked, useBackgroundColorClass } from 'owa-mail-list-item-shared';
import { MailListItem } from 'owa-mail-list-item';
import { mailListItemTouchBehavior } from 'owa-mail-list-swipe';
import { getBigHoverAction } from 'owa-surface-actions/lib/utils/getBigHoverAction';
import {
    getHoverSurfaceAction,
    getStore as getHoverSurfaceActionStore,
} from 'owa-surface-actions-option';
import getUnreadItemClass from '../selectors/getUnreadItemClass';
import { BigHoverAction } from '../lazyFunctions';
import { shouldShowBigHoverAction } from './BigHoverAction/utils/shouldShowBigHoverAction';
import { getDensityModeString } from 'owa-fabric-theme';
import classnames from 'owa-classnames';

import {
    backgroundChangeOnHover,
    backgroundChangeOnHover_deprecated,
    monarchUnselectedHover,
} from 'owa-mail-listitem-styles/lib/scss/MailListItem.scss';
import {
    showDeleteOnHover,
    mailListItem,
    unreadMailListItem,
    unreadMailListItemDarker,
    mailListItemRightPadding,
    mailListItemRPRightPadding,
} from 'owa-mail-listitem-styles/lib/scss/ListViewItemCommonStyles.scss';
import { isFeatureEnabled } from 'owa-feature-flags';
import { BigHoverActionPlaceholder } from 'owa-mail-list-view-bighoveraction';
import { isReadingPanePositionRight } from 'owa-mail-layout';

export const MailListItemWithoutTouchBehavior = observer(function (
    props: MailListItemWrapperProps
) {
    const {
        mailListItemDataProps,
        mailListTableProps,
        isFirstLevelExpanded,
        isSecondLevelExpanded,
        isLoadingSecondLevelExpansion,
        styleSelectorAsPerUserSettings,
        renderMinimum,
    } = props;

    const { tableViewId, isSingleLine } = mailListTableProps;

    const {
        canDelete,
        isFlagged,
        isInArchiveFolder,
        isInOutboxFolder,
        isPinned,
        isSelected,
        lastDeliveryTimestamp,
        rowKey,
        unreadCount,
    } = mailListItemDataProps;

    const tableView = getListViewStore().tableViews.get(tableViewId);

    const isRowExpanded = isFirstLevelExpanded || isSecondLevelExpanded;
    const showFocusSelectStyles = isFeatureEnabled('tri-mlFocusSelect-dev');
    const selectedNodeCount = useComputedValue(
        () => getListViewState().expandedConversationViewState?.selectedNodeIds?.length,
        []
    );

    const isChecked = useComputedValue(
        () => getIsChecked(tableView, rowKey, lastDeliveryTimestamp),
        [tableViewId, rowKey, lastDeliveryTimestamp]
    );

    const backgroundColorClass = useBackgroundColorClass(
        isSelected,
        isFlagged,
        isPinned,
        isChecked,
        mailListTableProps.supportsFlagging,
        mailListTableProps.supportsPinning,
        isRowExpanded,
        selectedNodeCount,
        showFocusSelectStyles
    );

    const unreadItemClass = getUnreadItemClass(
        mailListItemDataProps,
        mailListTableProps,
        isChecked,
        unreadMailListItem,
        unreadMailListItemDarker
    );

    const shouldApplyNewMCLHoverActionStyles =
        isFeatureEnabled('tri-mlRelocateHoverActions') && isSingleLine;

    const shouldBackgroundChangeOnHover =
        (!isSelected && !isChecked) || tableView?.isInVirtualSelectAllMode;

    const bigHoverAction = getBigHoverAction(
        getHoverSurfaceActionStore().hoverSurfaceActions ?? getHoverSurfaceAction()
    );

    const showBigHoverAction =
        !shouldApplyNewMCLHoverActionStyles &&
        bigHoverAction &&
        shouldShowBigHoverAction(bigHoverAction, canDelete, isInArchiveFolder, isInOutboxFolder);

    // Outer container CSS classes that are shared between 3 column and single line views
    const mailListItemOuterContainerClasses = classnames(
        mailListItem,
        shouldApplyNewMCLHoverActionStyles
            ? isReadingPanePositionRight()
                ? mailListItemRPRightPadding
                : mailListItemRightPadding
            : undefined,
        showBigHoverAction && showDeleteOnHover,
        backgroundColorClass,
        shouldBackgroundChangeOnHover
            ? showFocusSelectStyles
                ? backgroundChangeOnHover
                : backgroundChangeOnHover_deprecated
            : undefined,
        unreadCount > 0 && unreadItemClass,
        showBigHoverAction && !isSelected
            ? showFocusSelectStyles
                ? backgroundChangeOnHover
                : monarchUnselectedHover
            : undefined
    );

    return (
        <div className={mailListItemOuterContainerClasses} tabIndex={-1}>
            <MailListItem
                mailListTableProps={mailListTableProps}
                mailListItemDataProps={mailListItemDataProps}
                styleSelectorAsPerUserSettings={styleSelectorAsPerUserSettings}
                isFirstLevelExpanded={isFirstLevelExpanded}
                isSecondLevelExpanded={isSecondLevelExpanded}
                isLoadingSecondLevelExpansion={isLoadingSecondLevelExpansion}
                renderMinimum={renderMinimum}
            />
            {showBigHoverAction &&
                (renderMinimum ? (
                    <BigHoverActionPlaceholder />
                ) : (
                    <BigHoverAction
                        rowKey={rowKey}
                        tableViewId={tableViewId}
                        isSingleLine={isSingleLine}
                        isItemPart={false}
                        densityModeString={getDensityModeString()}
                    />
                ))}
        </div>
    );
},
'MailListItemWrapper');

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function mailListItemTouchBehavior called at module scope is not allowed. This may cause unwanted side effects. */
export const MailListItemWithTouchBehavior = mailListItemTouchBehavior(
    MailListItemWithoutTouchBehavior
);
